import { CRYPTOPUNKS, CRYPTOKITTIES, Zero, WeiPerEther, PREMIUM_DURATION, RESERVOIR_URL } from '@nftx/constants';
import { decodeEventLog, parseEther } from 'viem';
import config from '@nftx/config';
import { ERC20, NFTXEligibility, NFTXENSMerkleEligibility, NFTXVaultUpgradeable } from '@nftx/abi';
import { BadRequestError, hydrateResponse, hydrate } from '@nftx/errors';
import { query as query$2 } from '@nftx/query';

var makeFetchBlockNumberByTimestamp = (({
  fetch
}) =>
/**
 * Returns an approximate block number for a given timestamp
 */
async function fetchBlockNumberByTimestamp({
  timestamp
}) {
  const response = await fetch(`https://etherscan-api-proxy.nftx.workers.dev/?timestamp=${timestamp}`);
  const data = await response.json();
  const block = Number(data.result);
  return block;
});

/** Checks if 2 addresses are equal.
 * This is a rudimentary 0x address comparison function. It doesn't check if the addresses are valid or even defined, just that they are case-insensitivly equal
 */
const addressEqual = (a, b) => {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }
  return a.toLowerCase() === b.toLowerCase();
};
var addressEqual$1 = addressEqual;

function getContract({
  abi,
  address,
  provider,
  signer
}) {
  const read = new Proxy({}, {
    get(_target, functionName) {
      return ({
        args = []
      }) => {
        return provider.readContract({
          abi,
          address,
          functionName,
          args
        });
      };
    }
  });
  const write = new Proxy({}, {
    get(_target, functionName) {
      return async args => {
        if (!signer) {
          throw new Error('Cannot perform a write operation without a signer');
        }
        const [userAddress] = await signer.getAddresses();
        const account = userAddress;
        if (config.debug) {
          console.debug({
            method: functionName,
            contractAddress: address,
            account,
            ...args
          });
        }
        const {
          request
        } = await provider.simulateContract({
          address,
          abi,
          functionName,
          account,
          ...args
        });
        const hash = await signer.writeContract(request);
        return {
          hash,
          wait: () => provider.waitForTransactionReceipt({
            hash
          })
        };
      };
    }
  });
  const estimate = new Proxy({}, {
    get(_target, functionName) {
      return async args => {
        try {
          if (config.debug) {
            const method = ['estimate', functionName[0].toUpperCase(), functionName.slice(1)].join('');
            console.debug({
              method,
              contractAddress: address,
              ...args
            });
          }
          const gasEstimate = await provider.estimateContractGas({
            address,
            abi,
            functionName,
            ...args
          });
          return {
            gasEstimate
          };
        } catch (e) {
          console.error(e);
          return {
            gasEstimate: null
          };
        }
      };
    }
  });
  const contract = {
    read,
    write,
    estimate
  };
  return contract;
}

/** Return the user's balance of a given token */
const balanceOf = async args => {
  const {
    ownerAddress,
    provider,
    tokenAddress
  } = args;
  const contract = getContract({
    address: tokenAddress,
    abi: ERC20,
    provider
  });
  const result = await contract.read.balanceOf({
    args: [ownerAddress]
  });
  return result;
};
var balanceOf$1 = balanceOf;

/** Return the total supply of a given token */
const totalSupply = async ({
  provider,
  tokenAddress
}) => {
  const contract = getContract({
    address: tokenAddress,
    provider,
    abi: ERC20
  });
  const supply = await contract.read.totalSupply({});
  return supply;
};
var totalSupply$1 = totalSupply;

/** Takes a network/value key pair and returns the value for the current network */
const getChainConstant = (obj, network, fallback) => {
  return obj[network] ?? fallback;
};
var getChainConstant$1 = getChainConstant;

/** Searches an array of transaction logs, parses the found logs, and returns them */
const parseLogEvents = ({
  logs,
  signature,
  filter,
  abi
}) => {
  try {
    const events = logs.filter(log => {
      if (!log.topics[0]?.startsWith(signature)) {
        return false;
      }
      if (filter != null && !filter(log)) {
        return false;
      }
      return true;
    }).map(log => decodeEventLog({
      abi,
      ...log
    }).args);
    return events;
  } catch (e) {
    console.error(e);
    return [];
  }
};
var parseLogEvents$1 = parseLogEvents;

/** Searches an array of transaction logs, parses the found log, and returns its args */
const parseLogEvent = ({
  logs,
  signature,
  filter,
  abi
}) => {
  try {
    const events = parseLogEvents$1({
      abi,
      logs,
      signature,
      filter
    });

    // we want the find the _last_ log that matches
    const event = events[events.length - 1];
    if (!event) {
      return null;
    }
    return event;
  } catch (e) {
    console.error(e);
    return null;
  }
};
var parseLogEvent$1 = parseLogEvent;

const fetchBlockNumberByTimestamp = makeFetchBlockNumberByTimestamp({
  // Fun with possibly-undefined globals!
  fetch: typeof fetch !== 'undefined' ? fetch : undefined
});

/** Returns whether a given address is for the CryptoPunk collection */
const isCryptoPunk = address => {
  return addressEqual$1(address, CRYPTOPUNKS);
};

/** Returns whether a given address is for the CryptoKitty collection */
const isCryptoKitty = address => {
  return addressEqual$1(address, CRYPTOKITTIES);
};

/** Returns whether a given address is for a non-standard collection (CryptoPunks or CryptoKitties) */
const isNonstandard721 = address => {
  return isCryptoPunk(address) || isCryptoKitty(address);
};

const makeCheckEligible = ({
  getContract
}) =>
/** Checks if a series of token ids are eligible for minting
 * This is based on the vault's eligibility module
 * Also bear in mind that this does not deal with unprocessed merkle vaults
 * If you have a merkle vault, you'll need to call requiresProcessing on the assets to check their state
 * And processTokens to process them first
 * (Unprocessed assets will always just return false if passed in here first)
 */
async function checkEligible(args) {
  const {
    vault,
    provider,
    tokenIds
  } = args;
  if (!vault.eligibilityModule?.id) {
    return tokenIds.map(tokenId => ({
      tokenId,
      eligible: true
    }));
  }
  const results = await getContract({
    abi: NFTXEligibility,
    address: vault.eligibilityModule.id,
    provider
  }).read.checkEligible({
    args: [tokenIds.map(BigInt)]
  });
  return tokenIds.map((tokenId, i) => ({
    tokenId,
    eligible: !!results[i]
  }));
};
const checkEligible = makeCheckEligible({
  getContract
});
var checkEligible$1 = checkEligible;

const makeFetchMerkleLeaves = ({
  getContract,
  fetch
}) =>
/**
 * Fetches a list of eligible token ids for a vault with merkle eligibility rules
 */
async function fetchMerkleLeaves({
  provider,
  vault
}) {
  if (!vault?.eligibilityModule?.id) {
    return [];
  }
  const reference = vault.eligibilityModule.merkleReference;
  if (!reference) {
    return [];
  }
  let leaves = [];
  try {
    const uri = await getContract({
      abi: NFTXENSMerkleEligibility,
      address: vault.eligibilityModule.id,
      provider
    }).read.merkleLeavesURI({});
    leaves = await (await fetch(uri)).json();
  } catch (e) {
    console.error(e);
  }
  return leaves;
};
const fetchMerkleLeaves = makeFetchMerkleLeaves({
  getContract,
  fetch: typeof fetch === 'undefined' ? undefined : fetch
});
var fetchMerkleLeaves$1 = fetchMerkleLeaves;

const isMerkleVault = vault => {
  return vault?.eligibilityModule?.name?.toLowerCase().includes('merkleeligibility');
};
var isMerkleVault$1 = isMerkleVault;

const makeFetchMerkleReference = ({
  getContract
}) => async function fetchMerkleReference(args) {
  const {
    provider,
    vault
  } = args;
  if (!isMerkleVault$1(vault)) {
    return null;
  }
  if (!vault.eligibilityModule?.id) {
    return null;
  }
  const contract = getContract({
    abi: NFTXENSMerkleEligibility,
    address: vault.eligibilityModule?.id,
    provider
  });
  const reference = await contract.read.merkleReference({});
  return reference;
};
const fetchMerkleReference = makeFetchMerkleReference({
  getContract
});
var fetchMerkleReference$1 = fetchMerkleReference;

const makeProcessTokens = ({
  fetchMerkleLeaves
}) =>
/**
 * Checks specific token ids against a merkle eligibility module.
 * Once called, you must call the {@link checkEligible} method to get the eligibility state.
 * Do not call this method on a vault that doesn't implement a merkle module
 */
async function processTokens(args) {
  const {
    provider,
    signer,
    vault,
    tokenIds
  } = args;
  let {
    leaves
  } = args;
  if (!vault?.eligibilityModule?.merkleReference) {
    new BadRequestError('Not a valid eligibility module');
  }
  if (!leaves) {
    leaves = await fetchMerkleLeaves({
      provider,
      vault
    });
  }
  const {
    default: keccak256
  } = await import('keccak256');
  const {
    default: MerkleTree
  } = await import('merkletreejs');
  const tree = new MerkleTree(leaves.map(leaf => keccak256(leaf)), keccak256, {
    sortPairs: true
  });
  const proofs = tokenIds.map(tokenId => {
    return tree.getHexProof(keccak256(tokenId));
  });
  const contract = getContract({
    abi: NFTXENSMerkleEligibility,
    address: vault.eligibilityModule.id,
    provider,
    signer
  });
  return contract.write.processTokens({
    args: [tokenIds.map(BigInt), proofs]
  });
};
const processTokens = makeProcessTokens({
  fetchMerkleLeaves: fetchMerkleLeaves$1
});
var processTokens$1 = processTokens;

const makeRequiresProcessing = ({
  fetchMerkleLeaves
}) =>
/**
 * Checks if a set of token ids require processing
 * For each token that returns true, you will need to call {@link processTokens}
 */
async function requiresProcessing(args) {
  const {
    tokenIds,
    provider,
    vault
  } = args;
  let {
    leaves
  } = args;
  if (!tokenIds.length) {
    return [];
  }
  if (!vault?.eligibilityModule?.merkleReference) {
    return tokenIds.map(tokenId => ({
      tokenId,
      requiresProcessing: false
    }));
  }
  if (!leaves) {
    leaves = await fetchMerkleLeaves({
      provider,
      vault
    });
  }
  const contract = getContract({
    provider,
    abi: NFTXENSMerkleEligibility,
    address: vault.eligibilityModule.id
  });
  const {
    default: keccak256
  } = await import('keccak256');
  const {
    default: MerkleTree
  } = await import('merkletreejs');
  const tree = new MerkleTree(leaves.map(leaf => keccak256(leaf)), keccak256, {
    sortPairs: true
  });
  const proofs = tokenIds.map(tokenId => {
    return tree.getHexProof(keccak256(tokenId));
  });
  if (proofs.flat().length === 0) {
    // Failed to generate the proofs so don't waste gas on processing 0 transactions
    return tokenIds.map(tokenId => ({
      tokenId,
      requiresProcessing: false
    }));
  }
  const result = await Promise.all(tokenIds.map(async (tokenId, i) => {
    const proof = proofs[i];
    const result = await contract.read.requiresProcessing({
      args: [BigInt(tokenId), proof]
    });
    return {
      tokenId,
      requiresProcessing: result
    };
  }));
  return result;
};
const requiresProcessing = makeRequiresProcessing({
  fetchMerkleLeaves: fetchMerkleLeaves$1
});
var requiresProcessing$1 = requiresProcessing;

const ONE_DAY = 86400;
const ONE_WEEK = ONE_DAY * 7;
const ONE_MONTH = ONE_DAY * 30;
const ONE_YEAR = ONE_DAY * 365;
const LIQUIDITY_SHARE = 800000000000000000n; // 80%
const INVENTORY_SHARE = 200000000000000000n; // 20%

const getProRataMultiplier = (vaultAge, periodLength) => {
  if (vaultAge < periodLength) {
    // Vault isn't as old as the period we're measuring
    return Math.floor(ONE_YEAR / vaultAge);
  }
  if (periodLength < ONE_YEAR) {
    return Math.floor(ONE_YEAR / periodLength);
  }
  if (periodLength > ONE_YEAR) {
    return Math.floor(periodLength / ONE_YEAR);
  }
  return 1;
};
const calculateAprForPeriod = ({
  vaultAge,
  poolValue,
  periodFees,
  periodLength,
  share
}) => {
  if (!poolValue || !periodFees) {
    return Zero;
  }
  const proRataMultiplier = getProRataMultiplier(vaultAge, periodLength);

  // (periodFees * proRataMultiplier * 0.8%) / liquidity
  const numerator = periodFees * BigInt(proRataMultiplier) * share / WeiPerEther;
  const denominator = poolValue;
  const apr = numerator * WeiPerEther / denominator;
  return apr;
};
const calculateAprs = ({
  createdAt,
  periodFees,
  share = INVENTORY_SHARE,
  poolValue
}) => {
  const now = Math.floor(Date.now() / 1000);
  const vaultAge = now - createdAt;
  return {
    '24h': calculateAprForPeriod({
      vaultAge,
      periodFees: periodFees['24h'],
      periodLength: ONE_DAY,
      poolValue,
      share
    }),
    '7d': calculateAprForPeriod({
      vaultAge,
      periodFees: periodFees['7d'],
      periodLength: ONE_WEEK,
      share,
      poolValue
    }),
    '1m': calculateAprForPeriod({
      vaultAge,
      periodFees: periodFees['1m'],
      periodLength: ONE_MONTH,
      share,
      poolValue
    }),
    '1y': calculateAprForPeriod({
      vaultAge,
      periodFees: periodFees['1y'],
      periodLength: ONE_YEAR,
      share,
      poolValue
    })
  };
};
var calculateAprs$1 = calculateAprs;

const calculatePeriodFees = receipts => {
  const periodFees = {
    '1m': Zero,
    '24h': Zero,
    '7d': Zero,
    '1y': Zero
  };
  const now = Math.floor(Date.now() / 1000);
  const oneDayAgo = now - 86400;
  const oneWeekAgo = now - 604800;
  const oneMonthAgo = now - 2592000;
  const oneYearAgo = now - 31536000;
  receipts.forEach(receipt => {
    if (receipt.date >= oneDayAgo) {
      periodFees['24h'] += receipt.amount;
    }
    if (receipt.date >= oneWeekAgo) {
      periodFees['7d'] += receipt.amount;
    }
    if (receipt.date >= oneMonthAgo) {
      periodFees['1m'] += receipt.amount;
    }
    if (receipt.date >= oneYearAgo) {
      periodFees['1y'] += receipt.amount;
    }
  });
  return periodFees;
};
var calculatePeriodFees$1 = calculatePeriodFees;

const normalizeTokenIds = tokenIds => {
  return tokenIds.reduce((acc, item) => {
    if (Array.isArray(item) && item[0]) {
      return [...acc, [item[0], item[1] ?? 1]];
    }
    if (item) {
      return [...acc, [item, 1]];
    }
    return acc;
  }, []);
};

/** Extracts the ids given an array of target ids.
 * If an id has multiple quantities, the id is included multiple times in the output
 * @example ['1', '2', '3'] -> ['1', '2', '3']
 * @example [['1', 1], ['2', 2], ['3', 3]] -> ['1', '2', '2', '3', '3', '3']
 */
const getExactTokenIds = tokenIds => {
  return normalizeTokenIds(tokenIds).map(([id, quantity]) => {
    return Array(quantity).fill(id);
  }).flat();
};

/** Returns an array of unique target ids.
 * If an id has multiple quantities, it is only included once in the output
 * @example ['1', '2', '3'] -> ['1', '2', '3']
 * @example [['1', 1], ['2', 2], ['3', 3]] -> ['1', '2', '3']
 */
const getUniqueTokenIds = tokenIds => {
  return normalizeTokenIds(tokenIds).map(([id]) => id);
};

/** Returns an array of amounts for each token id
 * @example ['1', '2', '3'] -> [1, 1, 1]
 * @example [['1', 1], ['2', 2], ['3', 3]] -> [1, 2, 3]
 */
const getTokenIdAmounts = tokenIds => {
  return normalizeTokenIds(tokenIds).map(([, amount]) => {
    return amount;
  });
};

/** Returns the total amount of ids.
 * If an id has multiple quantities, it adds up the total quantity
 * @example ['1', '2', '3'] -> 3
 * @example [['1', 1], ['2', 2], ['3', 3]] -> 6
 */
const getTotalTokenIds = tokenIds => {
  return getExactTokenIds(tokenIds).length;
};

/** Takes a list of token ids and amounts and returns them in tuple format.
 * @example (['1', '2', '3'], [1, 2, 1]) -> [['1', 1], ['2', 2], ['3', 1]]
 * @example (['1', '2', '3']) -> [['1', 1], ['2', 1], ['3', 1]]
 * @example (['1', '1', '2', '3']) -> [['1', 2], ['2', 1], ['3', 1]]
 */
const zipTokenIds = (tokenIds, amounts) => {
  const grouped = {};
  tokenIds.forEach((tokenId, i) => {
    const count = amounts?.[i] ?? 1;
    grouped[tokenId] = (grouped[tokenId] ?? 0) + count;
  });
  return Object.entries(grouped);
};

const estimatePremiumPrice = ({
  network,
  holding,
  vTokenToEth,
  now
}) => {
  const premiumDuration = getChainConstant$1(PREMIUM_DURATION, network, 0);
  const premiumThreshold = now - premiumDuration;
  if (!holding || holding.dateAdded < premiumThreshold) {
    return [Zero, Zero];
  }
  const maxPremium = 5 * 10 ** 18; // 5 vTokens
  const timeStep = 60 * 60;
  const endValue = maxPremium * 2 ** (-premiumDuration / timeStep);
  // const endValue = 4882812500000000;
  const elapsed = now - holding.dateAdded;
  const p = maxPremium * 2 ** (-elapsed / timeStep) - endValue;
  // Bottom out at 0
  const premiumVTokenAmount = BigInt(Math.floor(Math.max(p, 0)));
  const price = premiumVTokenAmount * vTokenToEth / WeiPerEther;
  return [premiumVTokenAmount, price];
};
const estimateTotalPremiumPrice = ({
  holdings,
  tokenIds,
  vTokenToEth,
  now,
  network
}) => {
  return getExactTokenIds(tokenIds).reduce((total, tokenId) => {
    const holding = holdings?.find(h => h.tokenId === tokenId);
    const premium = estimatePremiumPrice({
      holding,
      vTokenToEth,
      now,
      network
    });
    return [total[0] + premium[0], total[1] + premium[1]];
  }, [Zero, Zero]);
};

const increaseByPercentage = (value, percentage) => {
  if (!value || !percentage) {
    return value;
  }
  const bigPercentage = parseEther(`${1 + percentage}`);
  const adjusted = value * bigPercentage / WeiPerEther;
  return adjusted;
};
const decreaseByPercentage = (value, percentage) => {
  if (!value || !percentage) {
    return value;
  }
  return increaseByPercentage(value, -percentage);
};

const fetchVTokenToEth = async ({
  provider,
  vaultAddress
}) => {
  const contract = getContract({
    abi: NFTXVaultUpgradeable,
    address: vaultAddress,
    provider
  });
  const vTokenToEth = await contract.read.vTokenToETH({
    args: [WeiPerEther]
  });
  return vTokenToEth;
};
var fetchVTokenToEth$1 = fetchVTokenToEth;

/** Returns whether a vault has swaps enabled (either random or target swaps count) */
const isVaultSwappable = vault => {
  return vault?.features?.enableSwap || false;
};

/** Returns whether a vault charges a fee for a target swap */
const doesVaultHaveTargetSwapFee = vault => {
  return (vault?.fees?.swapFee ?? Zero) !== Zero;
};

/**
 * Carries out a rudimentary check to see if a vault contains a given search string.
 */
const matchVault = (vault, search) => {
  if (!vault || !search) {
    return false;
  }
  const s = search.toLowerCase();
  return vault.token.name.toLowerCase().includes(s) || vault.token.symbol.toLowerCase().includes(s) || vault.asset.name.toLowerCase().includes(s) || vault.asset.symbol.toLowerCase().includes(s) || addressEqual$1(vault.id, s) || vault.vaultId === s || addressEqual$1(vault.asset.id, s);
};

/**
 * Returns any vaults that match a search string
 */
const searchVaults = (vaults, search) => {
  return vaults?.filter(vault => matchVault(vault, search));
};

const parseJson = str => {
  return JSON.parse(str, (key, value) => {
    if (typeof value === 'string' && value.startsWith('BigNumber(')) {
      const [, v] = value.match(/BigNumber\((.+)?\)/) ?? [];
      return BigInt(v);
    }
    return value;
  });
};
const stringifyJson = (obj, escapeBigNumber) => {
  return JSON.stringify(obj, (key, value) => {
    if (key === '_id') {
      return undefined;
    }
    if (typeof value === 'bigint') {
      if (escapeBigNumber) {
        return `BigNumber(${value})`;
      }
      return value.toString();
    }
    return value;
  });
};
const formatJson = (obj, escapeBigNumber) => {
  return JSON.parse(stringifyJson(obj, escapeBigNumber));
};

const query = async args => {
  const {
    url,
    data: _data,
    query: queryData = _data,
    headers = {},
    method = 'GET',
    cache,
    fetch
  } = args;
  try {
    return await query$2({
      url,
      cache,
      method,
      fetch,
      headers,
      data: queryData,
      stringify: value => stringifyJson(value, true),
      parse: parseJson
    });
  } catch (e) {
    if (e.response) {
      throw hydrateResponse({
        status: e.response.status,
        body: e.data
      });
    }
    throw hydrate(e);
  }
};
var query$1 = query;

const queryReservoir = ({
  path,
  network = config.network,
  ...rest
}) => {
  const base = getChainConstant$1(RESERVOIR_URL, network);
  const uri = new URL(path, base);
  const headers = {
    accept: '*/*'
  };
  const apiKey = getChainConstant$1(config.keys.RESERVOIR, network, null);
  if (apiKey) {
    headers['x-api-key'] = apiKey;
  } else {
    console.warn('No reservoir api key provided');
  }
  return query$1({
    url: uri.toString(),
    headers,
    ...rest
  });
};
var queryReservoir$1 = queryReservoir;

export { INVENTORY_SHARE, LIQUIDITY_SHARE, addressEqual$1 as addressEqual, balanceOf$1 as balanceOf, calculateAprs$1 as calculatePoolAprs, calculatePeriodFees$1 as calculatePoolPeriodFees, checkEligible$1 as checkEligible, decreaseByPercentage, doesVaultHaveTargetSwapFee, estimatePremiumPrice, estimateTotalPremiumPrice, fetchBlockNumberByTimestamp, fetchMerkleLeaves$1 as fetchMerkleLeaves, fetchMerkleReference$1 as fetchMerkleReference, fetchVTokenToEth$1 as fetchVTokenToEth, formatJson, getChainConstant$1 as getChainConstant, getContract, getExactTokenIds, getTokenIdAmounts, getTotalTokenIds, getUniqueTokenIds, increaseByPercentage, isCryptoKitty, isCryptoPunk, isMerkleVault$1 as isMerkleVault, isNonstandard721, isVaultSwappable, matchVault, normalizeTokenIds, parseJson, parseLogEvent$1 as parseLogEvent, parseLogEvents$1 as parseLogEvents, processTokens$1 as processTokens, query$1 as query, queryReservoir$1 as queryReservoir, requiresProcessing$1 as requiresProcessing, searchVaults, stringifyJson, totalSupply$1 as totalSupply, zipTokenIds };
